// src/lib/supabase.js  (o el archivo donde tengas este helper)

// URL base de tu backend (ajusta el .env o deja origin/api)
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api`;

// Helper para obtener la URL base actual (lo usabas en otros lados)
export function getBaseUrl() {
  return window.location.origin;
}

/**
 * ✅ MIGRACIÓN (1 sola vez):
 * En tu producción existe la clave legacy "auth token" (con espacio).
 * La normalizamos a "authToken" (sin espacio) para que todo sea consistente.
 */
(function migrateAuthTokenKey() {
  try {
    const legacy = localStorage.getItem("auth token"); // con espacio
    const current = localStorage.getItem("authToken"); // sin espacio
    if (!current && legacy) {
      localStorage.setItem("authToken", legacy);
      localStorage.removeItem("auth token");
    }
  } catch (e) {
    // No bloqueamos la app por esto
    console.warn("No se pudo migrar el token legacy:", e?.message || e);
  }
})();

// 🔐 Helper centralizado para obtener el token desde localStorage
export function getToken() {
  return (
    localStorage.getItem("auth_token") ||  // ✅ clave real
    localStorage.getItem("authToken") ||
    localStorage.getItem("auth token") ||
    ""
  );
}

/**
 * Wrapper genérico para llamar a tu backend.
 * - Añade automáticamente Authorization: Bearer <token> si existe.
 * - Maneja JSON (request/response).
 */
export async function apiFetch(path, options = {}) {
  const token = getToken();

  const headers = {
    Accept: "application/json",
    ...(options.headers || {}),
  };

  // Si vamos a mandar body como JSON
  if (options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    path.startsWith("http") ? path : `${API_BASE_URL}${path}`,
    {
      ...options,
      headers,
      credentials: "include", // por si también usas cookies
    }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Error HTTP ${response.status}`);
  }

  // Si no hay contenido, no intentes parsear JSON
  if (response.status === 204) return null;

  return response.json().catch(() => null);
}

/**
 * Obtener usuario actual desde el backend.
 * Supuesto: GET /auth/me → { user: {...}, role: "admin" }
 */
export async function getCurrentUser() {
  try {
    const data = await apiFetch("/auth/me", { method: "GET" });
    // Ajusta según lo que devuelva tu API
    return {
      user: data?.user || null,
      role: data?.role || data?.user?.role || null,
    };
  } catch (err) {
    console.warn("No se pudo obtener usuario actual:", err.message);
    return { user: null, role: null };
  }
}

/**
 * Login contra tu backend SQL.
 * Supuesto: POST /auth/login → { user, role, token }
 */
export async function loginRequest(email, password) {
  const data = await apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email: email.trim(), password }),
  });

  // Si te devuelven token, lo guardamos (normalizado)
  const token = data?.token || data?.access_token || data?.jwt || "";
  if (token) {
    localStorage.setItem("authToken", token);
    // limpia legado por si existe
    localStorage.removeItem("auth token");
  }

  return {
    user: data?.user || null,
    role: data?.role || data?.user?.role || null,
  };
}

/**
 * Logout contra tu backend.
 * Supuesto: POST /auth/logout
 */
export async function logoutRequest() {
  try {
    await apiFetch("/auth/logout", { method: "POST" });
  } catch (err) {
    console.warn("Error en logoutRequest:", err.message);
  } finally {
    // Borra token local pase lo que pase (ambas claves)
    localStorage.removeItem("authToken");
    localStorage.removeItem("auth token");
  }
}

/**
 * Equivalente a tu antiguo forceSignOut, pero sin Supabase.
 * Limpia token, cookies y redirige a /login.
 */
export async function forceSignOut() {
  try {
    // Intentar avisar al backend (si quieres)
    await logoutRequest();
  } catch (e) {
    // ignoramos errores aquí
  }

  // Limpia cualquier rastro de auth local
  localStorage.removeItem("authToken");
  localStorage.removeItem("auth token");
  localStorage.removeItem("auth user"); // ya que tu producción lo tiene

  // Si tenías otras claves relacionadas a auth, puedes borrarlas aquí
  // Object.keys(localStorage).forEach((key) => {
  //   if (key.includes("loquesea")) localStorage.removeItem(key);
  // });

  // Limpiar cookies (opcional, puede que no sea necesario)
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });

  // Redirigir al login
  window.location.href = "/login";
}
